import type { ChangeEventHandler } from "react"
import { useCallback, useMemo, useRef, useState } from "react"
import { navigate } from "gatsby"

import { type iconKeys, SvgIcon } from ".."
import { type FORM_VALUES } from "./_data"
import { useGeoSearchData } from "../../local/hooks/useGeoSearchData"
import {
  AutocompletePopover,
  Input,
  InputClearBtn,
  InputContainer,
  InputControl,
  InputIcon,
  InputLabel,
  ListItem,
} from "./_styles"

import { HeroSearchFormProps } from "./HeroSearchForm"

import { offset, shift, size } from "@floating-ui/react"
import { Unstable_Popup as BasePopup } from "@mui/base/Unstable_Popup"
import { useMediaQuery } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { debounce } from "lodash"
import { useFocusWithin } from "react-aria"
import { useController, useFormContext } from "react-hook-form"

const TYPE_ICONS: Record<string, iconKeys> = {
  city: "map",
  country: "globe",
  region: "location2",
  landmark: "pinMap2",
  state: "location2",
  destination: "destinationAfrica", // Will fall back to "map" if this icon doesn't exist
}

type GeoSearchOption = {
  label: string
  value: string
  type: "city" | "country" | "region" | "landmark" | "state" | "destination"
  slug: string
  external_id: number
  countryName?: string
  countryId?: string
  disabled?: boolean
}

const LocationInput = ({ variant }: HeroSearchFormProps) => {
  const { searchGeoOptions } = useGeoSearchData()

  // Form context hooks
  const { setFocus, setValue, resetField } = useFormContext<FORM_VALUES>()
  const {
    field: { name, value, onChange, onBlur, ref },
    fieldState: { isDirty },
  } = useController({
    name: "location",
  })

  // Use state to manage the input value for debouncing
  const [inputValue, setInputValue] = useState("")
  const [searchResults, setSearchResults] = useState<GeoSearchOption[]>([])

  // Memoize the rendered autocomplete options
  const renderedAutocompleteOptions = useMemo(() => {
    if (!inputValue || inputValue.length < 3) {
      return []
    }
    
    const results = searchGeoOptions(inputValue, 50) || []

    // Filter options to only those that START with the input value
    const filteredResults = results.filter((option) =>
      option.label.toLowerCase().startsWith(inputValue.toLowerCase())
    );
        
    // If no results are found, return an error message
    if (filteredResults.length === 0) {
        return [{
          label: "No locations found",
          value: "",
          type: "city" as const,
          slug: "",
          external_id: 0,
          disabled: true
        }]
      }

    return filteredResults
  }, [inputValue, searchGeoOptions])

  // Popper hooks
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [popupOpen, setPopupOpen] = useState(false)
  const [isFocusWithin, setFocusWithin] = useState(false)
  const [hasBlurred, setHasBlurred] = useState(false)
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      if (inputValue.length >= 3) {
        setPopupOpen(true)
      }
    },
    onBlurWithin: () => {
      setPopupOpen(false)
      // Clear input if no valid selection was made
      if (!renderedAutocompleteOptions.some((x) => x.label === inputValue && !x.disabled)) {
        resetField(name)
        setInputValue("")
      }
      if (inputValue !== "") setHasBlurred(true)
    },
    onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
  })
  
  const autocompletePopupId = popupOpen ? "autocomplete-popover" : undefined
  const [autocompleteFocusIndex, setAutocompleteFocusIndex] = useState<number | null>(null)
  const autocompleteListRef = useRef<Array<HTMLDivElement | null>>([])
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  // Handle the change event on the input
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newValue = event.target.value
    setInputValue(newValue)
    setFocus(name)
    
    // Only show results after 3 characters and keep showing them
    if (newValue.length >= 3) {
      setPopupOpen(true)
    } else {
      setPopupOpen(false)
      // Clear the form value if input is too short
      setValue("location", "")
    }

    // Don't auto-select exact matches - let user click to choose
    // This prevents the dropdown from disappearing when typing exact names
    setValue("location", "")
  }

  // Debounce the change event
  const handleDebouncedChange = useCallback(
    debounce((handleChange, e) => handleChange(e), 300),
    [renderedAutocompleteOptions]
  )

  // Handle the clear button click event
  const handleClear = () => {
    resetField(name)
    setInputValue("")
    setPopupOpen(false)
    onBlur()
    setFocusWithin(false)
  }

  // Handle the click event on the autocomplete list item
  const handleItemClick = (option: GeoSearchOption) => {
    if (option.disabled) return

    // Navigate based on entity type
    if (option.type === 'country') {
      // Countries go to trips page with country ID filter
      navigate(`/trips?country=${encodeURIComponent(option.value)}`)
    } else if (option.type === 'destination') {
      // Destinations go to trips page with destination ID filter
      navigate(`/trips?destination=${encodeURIComponent(option.value)}`)
    } else {
      // Cities, regions, states, landmarks go to their individual pages
      // Check if slug contains a domain (starts with http) or is a relative path
      if (option.slug.startsWith('http://') || option.slug.startsWith('https://')) {
        // External URL - use window.location
        window.location.href = option.slug
      } else {
        // Internal relative path - use Gatsby navigate
        // Ensure the slug starts with a forward slash
        const path = option.slug.startsWith('/') ? option.slug : `/${option.slug}`
        navigate(path)
      }
    }
  }

  return (
    <>
      <InputContainer
        ref={containerRef}
        focused={isFocusWithin}
        {...focusWithinProps}
        tabIndex={popupOpen ? 0 : -1}
        variant={variant}
        onClick={() => {
          setFocusWithin(true)
          setFocus(name)
        }}
      >
        <InputIcon>
          <SvgIcon variant="pinMap2" size={24} />
        </InputIcon>
        <InputControl variant={variant}>
          <StyledInput
            ref={ref}
            id="input-location"
            name={name}
            placeholder="Start typing to select a destination"
            autoComplete="off"
            spellCheck={false}
            value={inputValue || ""}
            onChange={(e) => {
              setInputValue(e.target.value)
              handleDebouncedChange(handleChange, e)
            }}
            onBlur={onBlur}
            onKeyDown={(e) => {
              if (inputValue.length < 3) return
              if (e.key === "ArrowDown") {
                e.preventDefault()
                setAutocompleteFocusIndex(0)
                autocompleteListRef.current?.[0]?.focus()
              }
              if (e.key === "ArrowUp") {
                e.preventDefault()
                setAutocompleteFocusIndex(renderedAutocompleteOptions.length - 1)
                autocompleteListRef.current?.[renderedAutocompleteOptions.length - 1]?.focus()
              }
            }}
          />
        </InputControl>
        {value !== "" && inputValue !== "" && isDirty && hasBlurred && (
          <InputClearBtn
            type="reset"
            focused={isFocusWithin}
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.currentTarget.blur()
            }}
          >
            <span>Clear</span>
            <SvgIcon variant="close" strokeMode size={8} />
          </InputClearBtn>
        )}
        <BasePopup
          id={autocompletePopupId}
          open={popupOpen && inputValue.length >= 3}
          anchor={containerRef.current}
          placement={isDesktop && variant === "default" ? "top" : "bottom"}
          strategy="fixed"
          disablePortal
          middleware={[
            shift(),
            offset(isDesktop ? 10 : 4),
            size({
              apply({ rects, availableHeight, elements }) {
                Object.assign(elements.floating.style, {
                  minWidth: `${rects.reference.width}px`,
                  height: isDesktop ? "" : `min(${availableHeight - 98}px, 50vh)`,
                  overflow: availableHeight >= elements.floating.clientHeight ? "" : "auto",
                })
              },
            }),
          ]}
          slotProps={{
            root: {
              role: "listbox",
              style: {
                zIndex: 1,
              },
            },
          }}
        >
          <AutocompletePopover sx={{ pt: 3 }}>
            {renderedAutocompleteOptions.map((option, index) => {
              const matches = match(option.label, inputValue || "", { insideWords: true })
              const parts = parse(option.label, matches)
              return (
                <LocationListItem
                  key={`${option.type}-${option.value}-${index}`}
                  ref={(node) => (autocompleteListRef.current[index] = node)}
                  tabIndex={option.disabled ? -1 : autocompleteFocusIndex === index ? 0 : -1}
                  disabled={option.disabled ?? false}
                  role="option"
                  aria-selected={option.label === inputValue}
                  selected={option.label === inputValue}
                  onClick={(e) => {
                    e.preventDefault()
                    handleItemClick(option)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleItemClick(option)
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault()
                      const nextIndex = Math.min(index + 1, renderedAutocompleteOptions.length)
                      if (nextIndex < renderedAutocompleteOptions.length) {
                        setAutocompleteFocusIndex(nextIndex)
                        autocompleteListRef.current?.[nextIndex]?.focus()
                      } else {
                        setAutocompleteFocusIndex(null)
                        setFocus(name)
                      }
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault()
                      const nextIndex = Math.max(index - 1, -1)
                      if (nextIndex >= 0) {
                        setAutocompleteFocusIndex(nextIndex)
                        autocompleteListRef.current?.[nextIndex]?.focus()
                      } else {
                        setAutocompleteFocusIndex(null)
                        setFocus(name)
                      }
                    }
                  }}
                >
                  <div className="list-item__icon">
                    <SvgIcon
                      className={`list-item__icon--${option.type}`}
                      variant={TYPE_ICONS[option.type] || "map"}
                      size={20}
                    />
                  </div>
                  <div className="list-item__content">
                    <div className="list-item__label">
                      {parts.map((part, partIndex) => (
                        <span key={partIndex} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                    <div className="list-item__type">
                      {option.type.charAt(0).toUpperCase() + option.type.slice(1)}
                    </div>
                  </div>
                  <div className="list-item__external-link">
                    <span className="external-link-text">See All Packages</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="15" 
                      height="15" 
                      viewBox="0 0 10 10" 
                      fill="#F05B2A"
                      className="external-link-icon"
                    >
                      <path d="M9 5L9.50253 4.5127L9.97507 5L9.50253 5.4873L9 5ZM1 5.7L0.3 5.7L0.3 4.3L1 4.3L1 5.7ZM5.12121 1L5.62374 0.512698L9.50253 4.5127L9 5L8.49747 5.4873L4.61868 1.4873L5.12121 1ZM9 5L9.50253 5.4873L5.62374 9.4873L5.12121 9L4.61868 8.5127L8.49747 4.5127L9 5ZM9 5L9 5.7L1 5.7L1 5L1 4.3L9 4.3L9 5Z" fill="#F05B2A"/>
                    </svg>
                  </div>
                </LocationListItem>
              )
            })}
          </AutocompletePopover>
        </BasePopup>
      </InputContainer>
    </>
  )
}

export default LocationInput

const LocationListItem = styled(ListItem)(({ theme }) => ({
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "space-between",
  "gap": theme.spacing(3),

  ".list-item__icon": {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    height: 20,
    width: 20,
  },
  
  ".list-item__content": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    flex: 1,
  },

  ".list-item__type": {
    ...theme.typography["caption"],
    color: theme.palette.text?.secondary || theme.palette.grey?.[600] || "#757575",
    opacity: 0.8,
  },

  ".list-item__external-link": {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
  },

  ".external-link-text": {
    marginRight: ".5rem",
    fontWeight: 600,
    fontSize: "15px",
    lineHeight: 1,
    opacity: 0,
    transition: "opacity 0.2s ease-in-out, color 0.2s ease-in-out",
    color: theme.palette.text?.secondary || "#757575",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  ".external-link-icon": {
    opacity: 0,
    transition: "opacity 0.2s ease-in-out, fill 0.2s ease-in-out",
    fill: theme.palette.text?.secondary || "#757575",
    display: "flex",
    alignItems: "center",
  },

  "&:hover .external-link-text, &:focus .external-link-text": {
    opacity: 1,
    color: "#F05B2A !important",
  },

  "&:hover .external-link-icon, &:focus .external-link-icon": {
    opacity: 1,
    fill: "#F05B2A !important",
  },
}))

const StyledInput = styled(Input)(({ theme }) => ({
  "&::placeholder": {
    fontSize: "18px",
  },
  "&:focus": {
    borderBottom: "none",
    outline: "none",
  },
  "&:focus-visible": {
    borderBottom: "none",
    outline: "none",
  },
}))
